import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { createPortal } from "react-dom";
import { Link } from "react-router";
import { Save, ChevronLeft } from "lucide-react";
import { apiFetch, ApiError } from "@/lib/api";
import { useAuth } from "@/app/auth-context";
import { PageContainer } from "@/app/layouts/PageContainer";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { LoadingState } from "@/components/loading-state";

export function MyProfilePage() {
  const { me } = useAuth();
  const qc = useQueryClient();
  const [s, setS] = useState({ firstName: "", lastName: "", phone: "", mobile: "", signature: "" });
  const [verifyCode, setVerifyCode] = useState("");

  const profileQuery = useQuery({
    queryKey: ["my-profile", me?.employeeId],
    queryFn: () => apiFetch<any>(`/api/employees/${me?.employeeId}`),
    enabled: !!me?.employeeId,
  });

  useEffect(() => {
    if (profileQuery.data) {
      setS({
        firstName: profileQuery.data.firstName ?? "",
        lastName: profileQuery.data.lastName ?? "",
        phone: profileQuery.data.phone ?? "",
        mobile: profileQuery.data.mobile ?? "",
        signature: profileQuery.data.signature ?? "",
      });
    }
  }, [profileQuery.data]);

  const patch = useMutation({
    mutationFn: (req: typeof s) => apiFetch("/api/me", { method: "PATCH", body: JSON.stringify(req) }),
    onSuccess: () => {
      toast.success("Profile saved");
      void qc.invalidateQueries({ queryKey: ["my-profile"] });
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
  });

  const enableMfa = useMutation({
    mutationFn: () => apiFetch<{ otpAuthUri: string; secret: string }>("/api/me/mfa/enable", { method: "POST" }),
  });

  const verifyMfa = useMutation({
    mutationFn: (code: string) =>
      apiFetch("/api/me/mfa/verify", { method: "POST", body: JSON.stringify({ code }) }),
    onSuccess: () => {
      toast.success("MFA enabled. You'll be challenged for a code on next sign-in.");
      setVerifyCode("");
      void qc.invalidateQueries({ queryKey: ["me"] });
    },
    onError: (err: Error) => toast.error(`Verify failed: ${err.message}`),
  });

  const [passwordOpen, setPasswordOpen] = useState(false);
  const [curPassword, setCurPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [passwordErr, setPasswordErr] = useState<string | null>(null);

  const changePassword = useMutation({
    mutationFn: () =>
      apiFetch("/api/me/password", {
        method: "POST",
        body: JSON.stringify({ currentPassword: curPassword, newPassword: newPassword }),
      }),
    onSuccess: () => {
      toast.success("Password updated successfully");
      setPasswordOpen(false);
      setCurPassword("");
      setNewPassword("");
      setPasswordErr(null);
    },
    onError: (e) => setPasswordErr(e instanceof ApiError ? e.message : "Failed to update password"),
  });

  const [portalNode, setPortalNode] = useState<HTMLElement | null>(null);
  useEffect(() => {
    setPortalNode(document.getElementById("topbar-left-portal"));
  }, []);

  return (
    <PageContainer>
      {portalNode && createPortal(
        <Link to="/admin" className="inline-flex items-center text-sm font-medium text-slate-500 hover:text-slate-900 transition-colors">
          <ChevronLeft className="mr-1 h-4 w-4" />
          Back
        </Link>,
        portalNode
      )}
      <div className="w-full space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-[#0f3453] tracking-tight">
            My Profile
          </h1>
          <p className="text-xs text-slate-500 mt-1">
            Manage your personal contact details, email signature, and account security.
          </p>
        </div>

        <div className="space-y-6">
            <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
                <CardTitle role="heading" aria-level={3} className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Profile Details
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                {profileQuery.isLoading ? (
                  <LoadingState />
                ) : (
                  <form
                    onSubmit={(e) => {
                      e.preventDefault();
                      patch.mutate(s);
                    }}
                    className="space-y-4"
                  >
                    <div className="space-y-4">
                      <div className="space-y-1.5">
                        <Label htmlFor="fn" className="text-xs font-bold text-slate-500 uppercase tracking-wider">First Name</Label>
                        <Input
                          id="fn"
                          value={s.firstName}
                          onChange={(e) => setS({ ...s, firstName: e.target.value })}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="ln" className="text-xs font-bold text-slate-500 uppercase tracking-wider">Last Name</Label>
                        <Input
                          id="ln"
                          value={s.lastName}
                          onChange={(e) => setS({ ...s, lastName: e.target.value })}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="ph" className="text-xs font-bold text-slate-500 uppercase tracking-wider">Phone</Label>
                        <Input
                          id="ph"
                          value={s.phone}
                          onChange={(e) => setS({ ...s, phone: e.target.value })}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="mo" className="text-xs font-bold text-slate-500 uppercase tracking-wider">Mobile</Label>
                        <Input
                          id="mo"
                          value={s.mobile}
                          onChange={(e) => setS({ ...s, mobile: e.target.value })}
                          className="h-9 text-sm"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <Label htmlFor="sig" className="text-xs font-bold text-slate-500 uppercase tracking-wider">Signature (HTML)</Label>
                        <textarea
                          id="sig"
                          rows={4}
                          value={s.signature}
                          onChange={(e) => setS({ ...s, signature: e.target.value })}
                          className="w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-ring min-h-[100px] border-slate-200"
                          placeholder="HTML signature goes here..."
                        />
                      </div>
                    </div>

                    <div className="flex justify-end pt-4 border-t border-slate-100">
                      <Button
                        type="submit"
                        disabled={patch.isPending}
                        className="bg-[#0f3453] text-white hover:bg-[#1a2f4c] shadow-sm font-semibold h-9 px-4 rounded-md flex items-center gap-2"
                      >
                        <Save className="h-4 w-4" />
                        {patch.isPending ? "Saving…" : "Save Profile"}
                      </Button>
                    </div>
                  </form>
                )}
              </CardContent>
            </Card>

            {/* Security Card */}
            <Card className="border border-slate-200 shadow-sm rounded-xl overflow-hidden bg-white">
              <CardHeader className="border-b border-slate-100 bg-slate-50/50 py-3 px-4">
                <CardTitle role="heading" aria-level={3} className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Security Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6 space-y-6">
                {/* Change Password Block */}
                <div className="space-y-2">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Password</h3>
                  <p className="text-xs text-slate-600 leading-relaxed">Update your account password regularly to keep your profile secure.</p>
                  <div className="pt-2">
                    <Button
                      onClick={() => setPasswordOpen(true)}
                      variant="outline"
                      className="h-9 px-4 font-semibold text-primary hover:bg-slate-50"
                    >
                      Change Password
                    </Button>
                  </div>
                </div>

                {/* MFA Block */}
                <div className="space-y-2 border-t border-slate-100 pt-6">
                  <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Multi-Factor Authentication (MFA)</h3>
                  <p className="text-xs text-slate-600 leading-relaxed">MFA adds an extra layer of protection to your account.</p>
                  
                  <div className="pt-2">
                    {me?.mfaEnabled ? (
                      <div className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">
                        <span className="h-2 w-2 rounded-full bg-emerald-500" />
                        MFA is Enabled
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <Button
                          onClick={() => enableMfa.mutate()}
                          disabled={enableMfa.isPending || !!enableMfa.data}
                          className="bg-[#0f3453] text-white hover:bg-[#1a2f4c] shadow-sm font-semibold h-9 px-4 rounded-md"
                        >
                          {enableMfa.isPending ? "Generating…" : "Enable MFA"}
                        </Button>

                        {enableMfa.data && (
                          <div className="space-y-4 rounded-xl border border-slate-200 p-4 bg-slate-50/50">
                            <p className="text-xs text-slate-600 leading-relaxed">
                              Scan the otpauth URL with an authenticator app (Google Authenticator, Authy, 1Password),
                              or enter the secret manually. Then enter the 6-digit verification code to complete setup.
                            </p>
                            <div className="bg-white border border-slate-200 rounded-lg p-2.5 space-y-1.5">
                              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">otpauth URL</div>
                              <div className="text-[10px] font-mono text-slate-600 break-all select-all">{enableMfa.data.otpAuthUri}</div>
                              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider border-t border-slate-100 pt-1.5 mt-1.5">Secret Key</div>
                              <div className="text-xs font-mono font-bold text-[#0f3453] select-all">{enableMfa.data.secret}</div>
                            </div>
                            
                            <div className="space-y-2">
                              <Label htmlFor="mfa-code" className="text-[10px] font-bold text-slate-500 uppercase tracking-wider">6-Digit Code</Label>
                              <div className="flex gap-2">
                                <Input
                                  id="mfa-code"
                                  inputMode="numeric"
                                  pattern="[0-9]{6}"
                                  maxLength={6}
                                  value={verifyCode}
                                  onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                                  placeholder="123456"
                                  className="h-9 text-sm max-w-[120px] font-mono text-center tracking-widest text-slate-700"
                                />
                                <Button
                                  onClick={() => verifyMfa.mutate(verifyCode)}
                                  disabled={verifyCode.length !== 6 || verifyMfa.isPending}
                                  className="bg-emerald-600 text-white hover:bg-emerald-700 shadow-sm font-semibold h-9 px-4 rounded-md"
                                >
                                  {verifyMfa.isPending ? "Verifying…" : "Verify & Enable"}
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
        </div>
      </div>

      <Dialog
        open={passwordOpen}
        onOpenChange={(o) => {
          setPasswordOpen(o);
          if (!o) {
            setCurPassword("");
            setNewPassword("");
            setPasswordErr(null);
          }
        }}
      >
        <DialogContent className="sm:max-w-[420px]">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-[#0f3453]">Change Password</DialogTitle>
          </DialogHeader>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              changePassword.mutate();
            }}
            className="space-y-4 pt-2"
          >
            <div className="space-y-1.5">
              <Label htmlFor="cp" className="text-xs font-bold text-slate-500 uppercase tracking-wider">Current Password</Label>
              <Input
                id="cp"
                type="password"
                value={curPassword}
                onChange={(e) => setCurPassword(e.target.value)}
                required
                className="h-9 text-sm"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="np" className="text-xs font-bold text-slate-500 uppercase tracking-wider">New Password</Label>
              <Input
                id="np"
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                minLength={12}
                required
                className="h-9 text-sm"
              />
              <p className="text-[10px] text-slate-500 leading-relaxed">
                Password must be at least 12 characters long.
              </p>
            </div>
            {passwordErr && (
              <p role="alert" className="text-xs text-destructive mt-1 font-medium">{passwordErr}</p>
            )}
            <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                onClick={() => setPasswordOpen(false)}
                className="h-9 text-sm font-semibold"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={changePassword.isPending}
                className="bg-[#0f3453] text-white hover:bg-[#1a2f4c] shadow-sm font-semibold h-9 px-4 rounded-md"
              >
                {changePassword.isPending ? "Updating…" : "Update Password"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </PageContainer>
  );
}
